import GuildSequencesContract from '../contracts/GuildSequencesContract.js';
import {lstorage} from 'utils/lstorage.js';
import accounts from 'accounts/Accounts.js';
import {ipfsHashToBytes32} from 'utils/ipfsHash.js';
import EthereumTransaction from 'decentralized/transactions/EthereumTransaction.js';
import {GUILD_SEQUENCES} from 'decentralized/transactions/contracts.js';
import BackendQuery from '../BackendQuery.js';
import {NewGuildSequence} from '../EthereumEvents.js';
import {stripLeadingZeroes, stripTrailingZeroes} from 'utils/strip.js';
import ipfs from '../Ipfs.js';

let contract = new GuildSequencesContract();

class GuildSequences {
    constructor(web3, guilds, encryption, crypto) {
        this.web3 = web3;
        this.encryption = encryption;
        this.crypto = crypto;
        this.guilds = guilds;
        this.contract = contract.getContract(web3);
        this.contractAddress = contract.contractAddress;
        this.NULL_BYTES_32 = web3.utils.padLeft('0x0', 64);
    }

    /**
     * @param sequenceData Unencrypted JSON of the sequence to be saved
     * @param guildId 
     * @param title Unencrypted title of sequence to be saved
     * @param previousHash Hash of previous sequence (if any)
     * Encrypts and saves the sequence to the passed guild
     */ 
    newGuildSequence(sequenceData, title, previousHash, guildId) {
        return new Promise(async (resolve, reject) => {
            // first need to encrypt this shit
            let keyData = await this.guilds.getGuildKey(guildId);
            let publicKey = keyData.rawPublicKey;
            
            let encryptedContent = await this.crypto.encryptRawContent(
                JSON.stringify(sequenceData), title, publicKey);
            let contentKey = encryptedContent.contentKey.contentKey;

            let sequenceHash = await ipfs.addJson(encryptedContent.encryptedContent)

            // now we use this data to save
            const account = await accounts.getAccount();
            if (account === undefined) {
                reject("No accounts");
            }
            
            let encryptedName = this.web3.utils.padLeft('0x' + encryptedContent.encryptedName, 64);
            let results = {
                contentHash: sequenceHash,
                previousHash,
                encryptedName,
                encryptedContentKey: encryptedContent.encryptedContentKey,
                publicKey: keyData.publicKey,
                guildId
            };
            lstorage.setItem("guildSequence." + sequenceHash, JSON.stringify(results));
            let json = {...sequenceData};
            lstorage.setItem("projectData", JSON.stringify(json));
            lstorage.setItem("projectHash", sequenceHash);

            new EthereumTransaction(
                GUILD_SEQUENCES,
                'editSequence', 
                [
                    previousHash ? ipfsHashToBytes32(previousHash) : this.NULL_BYTES_32,
                    ipfsHashToBytes32(sequenceHash),
                    encryptedName,
                    encryptedContent.encryptedContentKey,
                    keyData.publicKey,
                    guildId]).execute();

            resolve(sequenceHash);
        });
    }

    async getGuildSequence(contentHash) {
        let query = new BackendQuery(this.contractAddress)
            .select('contentHash')
            .select('previousHash')
            .select('user')
            .select('encryptedName')
            .select('guildId')
            .select('encryptedContentKey')
            .select('publicKey')
            .from(NewGuildSequence)
            .where('contentHash', contentHash);

        let results = await query.executeQuery();

        if (results.length === 0) {
            let key = "guildSequence." + contentHash;
            let litem = lstorage.getItem(key);
            if (litem) {
                litem = JSON.parse(litem);
                results = [litem];
            } else {
                return undefined;
            }
        }

        let result = results[0];
        // get guildId
        let guildId = result.guildId
        let guildKey = await this.guilds.getGuildKey(guildId);
        return this._decryptQueryResult(result, guildKey.privateKey);
    }

    async getGuildSequences(guildId, showAll=true) {
        let query = new BackendQuery(this.contractAddress)
            .select('contentHash')
            .select('previousHash')
            .select('user')
            .select('encryptedName')
            .select('encryptedContentKey')
            .select('publicKey')
            .select('guildId')
            .from(NewGuildSequence)
            .where('guildId', guildId);

        if (!showAll) {
            // we should try to filter by current user
            const account = await accounts.getAccount();
            if (account !== undefined) {
                query.where('address', account.toLowerCase());
            }
        }

        let encryptedResults = await query.executeQuery();
        if (encryptedResults.length === 0) {
            return [];
        }
        let guildKey = await this.guilds.getGuildKey(guildId);
        if (guildKey === undefined) {
            return [];
        }
        let decryptedResults = [];
        for (let i in encryptedResults) {
            decryptedResults.push(await this._decryptQueryResult(
                encryptedResults[i], guildKey.privateKey));
        }
        return decryptedResults;
    }

    async _decryptQueryResult(result, privateKey) {
        if (!privateKey) {
            let guildKey = await this.guilds.getGuildKey(result.guildId);
            if (!guildKey) {
                return undefined;
            }
            privateKey = guildKey.privateKey;
        }
        let encryptedContentKey = stripTrailingZeroes(result.encryptedContentKey);

        let title = await this.crypto.decryptName(result.encryptedName, encryptedContentKey, privateKey)
        let user = result.user;
        let contentKey = await this.crypto.decryptContentKey(encryptedContentKey, privateKey);
        let ret = {
            title:  stripTrailingZeroes(title),
            encryptedTitle:  result.encryptedName,
            user: user,
            newSequence: result.contentHash,
            previousSequence: result.previousHash,
            guildId: result.guildId,
            contentKey: contentKey ? contentKey.contentKey : undefined,
            blockNumber: result.blockNumber,
            publicKey: result.publicKey && stripTrailingZeroes(result.publicKey),
        };

        return ret;
    }
}

export default GuildSequences;
